import {
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	useId,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	GetFieldValue,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import useSWR from "swr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { ReorderSections } from "./ReorderSections"
import { TTripVoucherTemplate } from "./store"

type ITripVoucherTemplateForm = TTripVoucherTemplate

export function XHR(xhr: XHRInstance) {
	return {
		async getTripVoucherTemplateData(): Promise<Array<TTripVoucherTemplate>> {
			return xhr.get(`/trip-voucher-templates`).then(({ data }) => data.data)
		},
		async getTripVoucherTemplateEditData(): Promise<TTripVoucherTemplate> {
			return xhr.get(`trip-voucher-templates/create`).then(({ data }) => {
				return data.data
			})
		},
	}
}

export function TripVoucherTemplates() {
	const xhr = useXHR()
	const { data, mutate, isLoading } = useSWR(`/trip-voucher-templates`, () =>
		XHR(xhr).getTripVoucherTemplateData()
	)
	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	if (isLoading) {
		return <Spinner alignCenter padding="4" />
	}

	return (
		<Stack>
			<Inline alignItems="center" justifyContent={"between"}>
				<Inline alignItems={"center"} justifyContent={"center"} gap={"2"}>
					<Box
						padding={"3"}
						rounded="full"
						borderColor={"default"}
						borderWidth={"1"}
					>
						<Icons.Ticket color={"muted"} size={"10"} />
					</Box>
					<Stack>
						<Heading as="h3">Trip Voucher Template Configurations</Heading>
						<Text>
							These configuration will be applied to Trip voucher only.
						</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button onClick={openDialog} status="primary" size="sm">
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
				<Dialog
					open={isDialogOpen}
					onClose={closeDialog}
					title="Update Trip Voucher"
					lg
				>
					<Dialog.Body>
						<EditTripVoucherTemplate
							onCancel={() => {
								closeDialog()
							}}
							onSuccess={() => {
								closeDialog()
								mutate()
							}}
						/>
					</Dialog.Body>
				</Dialog>
			</Inline>
			<Divider sm />
			{!data || data?.length === 0 ? (
				<Box padding="4" bgColor="default" rounded="md" borderWidth="1">
					<Text>Using default trip voucher template configurations.</Text>
				</Box>
			) : (
				<Stack gap="4">
					<Inline>
						<Text color={"default"} fontWeight={"semibold"}>
							Title Customization:
						</Text>{" "}
						<Text color={"muted"}>{data[0].title}</Text>
					</Inline>
					<Inline>
						<Text color={"default"} fontWeight={"semibold"}>
							Show Check-in/Check-out Time:
						</Text>{" "}
						<Text color={"muted"}>
							{data[0].show_checkin_checkout_time ? "Yes" : "No"}
						</Text>
					</Inline>
					<Stack gap={"2"}>
						<Text fontWeight="semibold">
							<Icons.SwitchHorizontal rotate="90" />
							Show, Hide & Reorder Sections
						</Text>
						<Stack
							gap="4"
							padding="4"
							rounded="md"
							bgColor="default"
							borderWidth="1"
						>
							<Stack gap={"2"}>
								{data[0].section_ordering_settings.map((element, index) =>
									element.hide === 0 ? (
										<ReorderItem
											element={element.title}
											order={index + 1}
											key={element.order}
										/>
									) : null
								)}
							</Stack>
						</Stack>
					</Stack>
				</Stack>
			)}
		</Stack>
	)
}

function ReorderItem({ element, order }: { element: string; order: number }) {
	return (
		<Inline gap="2" alignItems="center">
			<Stack
				size={"6"}
				alignItems="center"
				justifyContent="center"
				borderWidth="1"
				rounded="full"
				bgColor="success_emphasis"
				color="on_emphasis"
			>
				<Text fontWeight="semibold">{order}</Text>
			</Stack>
			<Box flex="1" minWidth="0">
				<Box
					paddingX={"4"}
					paddingY={"2"}
					borderWidth="1"
					rounded="md"
					maxWidth={"xs"}
				>
					<Inline gap="4" alignItems={"center"} justifyContent="between">
						<Text>{element}</Text>
					</Inline>
				</Box>
			</Box>
		</Inline>
	)
}

const validationSchema = validateFormValues(
	Validator.object().shape({
		title: Validator.string().required("Please enter title name"),
		show_checkin_checkout_time: Validator.boolean(),
	})
)

function EditTripVoucherTemplate({
	onCancel,
	onSuccess,
}: {
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()

	const id__ = useId()
	const { data } = useSWR(`/trip-voucher-templates/create?${id__}`, () =>
		XHR(xhr).getTripVoucherTemplateEditData()
	)
	if (!data) {
		return <Spinner alignCenter padding="4" />
	}
	const initialValues = {
		title: data.title,
		show_checkin_checkout_time: Boolean(data.show_checkin_checkout_time),
		section_ordering_settings: data.section_ordering_settings || [],
	}
	return (
		<Form<ITripVoucherTemplateForm>
			onSubmit={withServerErrors(async (values) => {
				const { title, show_checkin_checkout_time, section_ordering_settings } =
					values
				await xhr.post(`trip-voucher-templates`, {
					title,
					show_checkin_checkout_time: show_checkin_checkout_time ? 1 : 0,
					section_ordering_settings: (section_ordering_settings || []).map(
						(v, i) => ({
							order: i + 1,
							title: v.title,
							section: v.section,
							hide: v.hide,
						})
					),
				})
				onSuccess()
				showSnackbar("Successfully Changed")
			})}
			subscription={{ submitting: true }}
			initialValues={initialValues}
			validate={validationSchema}
		>
			{({ handleSubmit, submitting, form }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="6" paddingY="4">
						<Stack gap="4">
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading fontSize="md">Title Customization</Heading>
										<Text color="muted">
											Configure title settings like Tour Voucher, Final Trip
											Voucher and Service Voucher.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Component initialState={false}>
										{({ state: editing, setState }) =>
											!editing ? (
												<Stack gap="1">
													<Text fontWeight="semibold">Section Title</Text>
													<Inline
														borderWidth="1"
														rounded="md"
														paddingLeft="4"
														bgColor="subtle"
														gap="4"
														justifyContent="between"
														alignItems="center"
														maxWidth={"sm"}
													>
														<Text>{data.title}</Text>
														<Button
															onClick={() => {
																setState(true)
															}}
															level="tertiary"
														>
															<Icons.Pencil />
														</Button>
													</Inline>
												</Stack>
											) : (
												<TextInputField
													name="title"
													label="Section Title"
													type="text"
													maxWidth={"sm"}
												/>
											)
										}
									</Component>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading fontSize="md">
											Show Check-in & Check-out Time
										</Heading>
										<Text color="muted">
											Configure whether check-in and check-out times should be
											displayed in trip vouchers
										</Text>
									</Stack>
								</Col>
								<Col>
									<SwitchInputField
										name="show_checkin_checkout_time"
										label="Show Check in check out"
										help="Display check-in and check-out times in trip vouchers"
									/>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading fontSize="md">
											Show, Hide & Reorder Sections
										</Heading>
										<Text color="muted">
											Choose which sections appear in the trip voucher and set
											their order. Move sections to the right in the sequence
											you want them to appear. Sections left on the left will be
											hidden from the trip voucher.
										</Text>
									</Stack>
								</Col>
								<Col sm={12} md={4}>
									<GetFieldValue<
										ITripVoucherTemplateForm["section_ordering_settings"]
									>
										name={"section_ordering_settings"}
									>
										{({ value: sections }) => {
											return (
												<Stack gap={"2"}>
													{(sections || data.section_ordering_settings).map(
														(element, index) =>
															element.hide === 0 ? (
																<ReorderItem
																	key={element.order}
																	element={element.title}
																	order={index + 1}
																/>
															) : null
													)}
													<Box flex="1" paddingLeft={"8"}>
														<ReorderSections
															sections={
																sections || data.section_ordering_settings
															}
															onSuccess={(value) => {
																form.change("section_ordering_settings", value)
															}}
															allowHide
														>
															{({ reOrder }) => (
																<Button status="primary" onClick={reOrder}>
																	<Icons.SwitchHorizontal rotate="90" />
																	Manage Sections
																</Button>
															)}
														</ReorderSections>
													</Box>
												</Stack>
											)
										}}
									</GetFieldValue>
								</Col>
							</Grid>
						</Stack>
						<Divider />
						<Stack>
							<SubmissionError />
							<Inline justifyContent="end" gap="2">
								<Button onClick={onCancel}>Cancel</Button>
								<Button status="primary" type="submit" disabled={submitting}>
									{submitting ? "Please wait..." : "Save Changes"}
								</Button>
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}
